// Everything the Student Reports table decides about itself that is not React.
//
// Feature #48 (OKO2-3036 + OKO2-3037). The rules a teacher can see — what an
// unknown cell reads, how Solo Time is spelled, which columns sort, what the
// row click links to, what the CSV contains — live here as pure functions, so
// they are pinned by tests that cannot pass against the wrong column.
//
// ONE RULE RUNS THROUGH ALL OF IT: MISSING IS NEVER ZERO. "They did nothing"
// and "we do not know" are different facts, and a zero level sits BELOW Level 1
// and reads as "this child is failing" when it means "we have no evidence".

import { constants } from "utils";

export const EM_DASH = "—";

// The two ends of the collaboration/maths rubric carry a mark of their own on
// this screen, matching the Key frame above the table.
const LEVEL_MARKS = { 1: "⚠ ", 4: "★ " };

/** No level: the one test the screen, the CSV and the PDF all use. */
export const isUnknownLevel = (level) => level === null || level === undefined || level === "";

/**
 * A level as the CSV states it: "★ Level 4", "Level 2", "⚠ Level 1", or an em
 * dash. The mark is TEXT because a CSV cannot draw the tag's glyph.
 *
 * On screen use `levelLabel`: the tag renders the glyph itself, and a marked
 * label inside it prints the mark twice.
 */
export const levelText = (level) => {
  if (isUnknownLevel(level)) return EM_DASH;
  return `${LEVEL_MARKS[level] || ""}Level ${level}`;
};

/** A level as the tag states it: the words only, never the mark. */
export const levelLabel = (level) => {
  if (isUnknownLevel(level)) return EM_DASH;
  return `Level ${level}`;
};

/** A headline cell. Blank prose is an unknown, not an empty sentence. */
export const headlineText = (text) =>
  text && String(text).trim() ? String(text) : EM_DASH;

const hasSoloEvidence = (row) =>
  !row?.not_applicable &&
  !row?.notApplicable &&
  row?.solo_accuracy !== null &&
  row?.solo_accuracy !== undefined &&
  row.solo_answered > 0;

/**
 * Solo Accuracy: "11 of 12" — correct of answered, in words, no percentage.
 *
 * A session's denominator is tiny, and "100%" over three questions claims a
 * precision the evidence cannot carry. The percentage stays on the KPI tile,
 * whose denominator is the whole timeline.
 *
 * `solo_accuracy` is still what decides KNOWN from UNKNOWN. It is the field the
 * compiler sets when it scored the session at all, so a row without it has no
 * solo evidence — and that is an em dash, never "0 of 0". A known row with
 * nothing answered is the same unknown: it has no fraction to state.
 */
export const soloTimeText = (row) => {
  if (!hasSoloEvidence(row)) return EM_DASH;
  return `${row.solo_correct || 0} of ${row.solo_answered}`;
};

const HIGH_BAND_PERCENT = 80;
const MID_BAND_PERCENT = 30;

/**
 * The Solo Accuracy pill's band: "high", "mid" or "low".
 *
 * Decided on the two counts in integer arithmetic, so 3 of 10 is exactly 30%
 * and nothing is rounded before it is banded. `solo_accuracy` is not read: it
 * is the compiler's own number and is not guaranteed to equal the counts.
 */
export const soloBand = (row) => {
  if (!hasSoloEvidence(row)) return null;
  const correct = row.solo_correct || 0;
  const answered = row.solo_answered;
  if (100 * correct >= HIGH_BAND_PERCENT * answered) return "high";
  if (100 * correct >= MID_BAND_PERCENT * answered) return "mid";
  return "low";
};

/** An average level, to one decimal place. Never a zero standing in for null. */
export const averageLevelText = (value) =>
  value === null || value === undefined ? EM_DASH : Number(value).toFixed(1);

/**
 * The Total Session Time tile: whole minutes, uppercased by the tile's own
 * styling. A measured 0 keeps its zero; an absent total does not.
 */
export const minutesText = (value) =>
  value === null || value === undefined ? EM_DASH : `${Math.round(value)} MIN`;

/**
 * The Dosage tile's value, split into number/unit pairs.
 *
 * Parts rather than a string because the two sizes ARE the design: frame
 * 4485:72295 sets the digits at 32px and the "h" and "m" at 24px, then
 * uppercases the lot. Joining them into "1h 6m" here would throw away the only
 * information the caller needs to draw it.
 */
export const dosageParts = (value) => {
  if (value === null || value === undefined) return null;
  const total = Math.max(0, Math.round(value));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  const parts = [];
  if (hours) parts.push({ value: String(hours), unit: "h" });
  // A student with under an hour still gets a minutes part, and so does one
  // with exactly zero: "0m" is a measurement, an empty tile is not.
  if (minutes || !hours) parts.push({ value: String(minutes), unit: "m" });
  return parts;
};

/**
 * "7/15/26 - Today" -- the window the strip's four numbers were measured over.
 *
 * Read off the session rows, because the summary carries no dates: its averages
 * are measured over exactly these sessions, so their oldest date IS
 * the start of the window. No rows, no claim -- the caller drops the line.
 */
export const summaryRangeText = (rows) => {
  const times = (rows || [])
    .map((row) => (row?.started_at ? new Date(row.started_at).getTime() : NaN))
    .filter(Number.isFinite);
  if (!times.length) return null;
  const oldest = new Date(Math.min(...times));
  const year = String(oldest.getFullYear()).slice(2);
  return `${oldest.getMonth() + 1}/${oldest.getDate()}/${year} - Today`;
};

/** A percentage KPI. A measured 0% keeps its zero; an absent one does not. */
export const percentText = (value) =>
  value === null || value === undefined ? EM_DASH : `${Math.round(value)}%`;

// The denominators. VISIBLE TEXT beneath the value, never a hover and never a
// footnote: a hover is unreachable by keyboard and by touch and invisible when
// scanning. "Level 2.1 over three of five sessions" and "Level 2.1 over five of
// five" are different claims.
export const answeredCountLine = (correct, answered) =>
  `${correct || 0} of ${answered || 0} answered`;

export const sessionCountLine = (contributing, total) =>
  `from ${contributing || 0} of ${total || 0} sessions`;

/**
 * The Session Date cell, split into the date over the time.
 *
 * Formatted from the raw timestamp IN THE BROWSER, exactly as `getSessionDate`
 * does for the Session Reports row this page links to, so the two surfaces
 * cannot name different days for the same session.
 */
export const sessionDateParts = (startedAt) => {
  if (!startedAt) return { date: EM_DASH, time: "" };
  const date = new Date(startedAt);
  if (isNaN(date.getTime())) return { date: EM_DASH, time: "" };
  return {
    date: date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }),
    time: date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }),
  };
};

/**
 * The Group Focus string, assembled ONCE for both the cell and the CSV.
 *
 * The parts come from `useSessionFocus`; this only decides how they read on one
 * line, so the file cannot spell the cell differently from the screen (D48.21).
 */
export const groupFocusText = ({ code, countLabel, title } = {}) => {
  const head = [code, countLabel].filter(Boolean).join(" · ");
  // With nothing else to name it the helper titles the row with its own code,
  // which must not render as a code explained by itself.
  const tail = title && title !== code ? title : null;
  // A PLAIN SPACE, and never an em dash: on this screen an em dash means "we do
  // not know", and a title line beginning with one reads as a missing value.
  // The screen puts the title on its own line, where the space collapses; the
  // CSV puts it on one, where the space is the only thing separating them — so
  // the cell and the file stay byte-identical.
  return [head, tail].filter(Boolean).join(head && tail ? " " : "");
};

// ---- the sort ------------------------------------------------------------
//
// Client-side, on four columns. The two Headlines do not sort: ordering a table
// by prose orders nothing a teacher wants.
//
// ⚠ THIS IS FREE ONLY BECAUSE THE ENDPOINT RETURNS EVERY ROW. If pagination
// ever lands, this sorts THE LOADED PAGE and says nothing — a wrong answer that
// looks exactly like a right one. Whoever adds pagination owns moving the sort
// (and the CSV) server-side.

export const SORT_KEYS = {
  SESSION_DATE: "session_date",
  GROUP_FOCUS: "group_focus",
  SOLO_TIME: "solo_time",
  COLLAB_LEVEL: "collab_level",
};

export const SORTABLE_COLUMNS = [
  SORT_KEYS.SESSION_DATE,
  SORT_KEYS.GROUP_FOCUS,
  SORT_KEYS.SOLO_TIME,
  SORT_KEYS.COLLAB_LEVEL,
];

/** Ascending, descending, then back to the server's own order. */
export const nextSortState = (current, column) => {
  if (!current || current.column !== column) return { column, direction: "asc" };
  if (current.direction === "asc") return { column, direction: "desc" };
  return null;
};

const sortValue = (row, column, focusLabels) => {
  switch (column) {
    case SORT_KEYS.SESSION_DATE:
      return row.started_at ? new Date(row.started_at).getTime() : null;
    case SORT_KEYS.GROUP_FOCUS:
      // Before the classroom bundles resolve there is no label to sort on, so
      // the raw prefix holds the order and the rows settle once they arrive.
      return focusLabels?.[row.session_id] || row.standard_prefix || null;
    case SORT_KEYS.SOLO_TIME:
      return hasSoloEvidence(row) ? (row.solo_correct || 0) / row.solo_answered : null;
    case SORT_KEYS.COLLAB_LEVEL:
      return row.student_collab_level;
    default:
      return null;
  }
};

// Standard codes carry numbers, so "5.NBT.B.7" has to sort before "5.NBT.B.10".
const naturalOrder = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

const compareValues = (left, right) => {
  if (typeof left === "string" && typeof right === "string") {
    return naturalOrder.compare(left, right);
  }
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
};

const isUnknown = (value) => value === null || value === undefined || value === "";

/**
 * Reorder the rows for display. Never mutates, never drops, never refetches.
 *
 * Rows with no value for the sorted column are grouped at ONE END regardless of
 * direction: without that their order is undefined, no test can pin it, and the
 * test that covers the sort gets disabled.
 */
export const sortRows = (rows, sort, focusLabels = {}) => {
  if (!sort?.column) return [...(rows || [])];

  const factor = sort.direction === "desc" ? -1 : 1;
  return [...rows]
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const left = sortValue(a.row, sort.column, focusLabels);
      const right = sortValue(b.row, sort.column, focusLabels);
      if (isUnknown(left) && isUnknown(right)) return a.index - b.index;
      if (isUnknown(left)) return 1;
      if (isUnknown(right)) return -1;
      return compareValues(left, right) * factor || a.index - b.index;
    })
    .map(({ row }) => row);
};

// ---- the row click -------------------------------------------------------

/**
 * The Session Reports deep link for one row.
 *
 * Carries the session, the SELECTED STUDENT (D48.15 — without it the teacher
 * lands on an unfiltered list and has to find the child again, on a page whose
 * whole purpose was to be about that child) and the class when one is chosen.
 *
 * `date_range=all_time` is carried for one reason, recorded because it looks
 * like an invented filter and is the opposite: the destination DEFAULTS to
 * `last_4_weeks` when told nothing, and a student report's value is sessions
 * from weeks and months ago, so a link with no window at all hides exactly the
 * rows this page exists to show. All time REMOVES a window rather than applying
 * one. The shipped Proficiency page's `sessionHistoryUrl` answers the identical
 * problem the same way. No standard code and no start/end window are carried:
 * this page never sets either.
 */
export const sessionReportUrl = ({ sessionId, studentId, classroomId } = {}) => {
  const params = new URLSearchParams();
  if (sessionId) params.set("session_id", sessionId);
  if (studentId) params.set("student_id", studentId);
  if (classroomId) params.set("classroom_id", classroomId);
  params.set("date_range", "all_time");
  // Session Reports opens By Lesson by default; this link is about sessions.
  params.set("tab", "groups");
  return `${constants.SESSION_HISTORY_PATH}?${params.toString()}`;
};

// ---- the CSV -------------------------------------------------------------
//
// Built in the browser from the rows already held, so THE FILE AND THE SCREEN
// CANNOT DISAGREE. There is no export endpoint: column 2 is resolved by a React
// hook and a server cannot run one.

export const CSV_HEADERS = [
  "Session Date",
  "Group Focus",
  "Solo Accuracy",
  "Math Headline",
  "Collaboration Level",
  "Collaboration Headline",
];

// An unknown is ONE consistent empty cell, in every one of the six columns.
// An em dash is what a human reading the file wants and what a spreadsheet
// cannot average; the empty string is the choice, applied without exception.
const csvValue = (text) => (text === EM_DASH ? "" : text);

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvLine = (cells) => cells.map(csvCell).join(",");

const csvRow = (row, focusLabels) => {
  const { date, time } = sessionDateParts(row.started_at);
  return [
    csvValue([date, time].filter(Boolean).join(" ")),
    focusLabels?.[row.session_id] || row.standard_prefix || "",
    csvValue(soloTimeText(row)),
    csvValue(headlineText(row.student_math_rationale)),
    csvValue(levelText(row.student_collab_level)),
    csvValue(headlineText(row.student_collab_rationale)),
  ];
};

// Pooled from the same counts each row's cell reads, never from the stored
// `solo_accuracy`, so the file's average cannot disagree with its own rows.
const pooledSoloCounts = (rows) =>
  rows.filter(hasSoloEvidence).reduce(
    (total, row) => ({
      correct: total.correct + (row.solo_correct || 0),
      answered: total.answered + row.solo_answered,
    }),
    { correct: 0, answered: 0 }
  );

/**
 * The file: the rows as displayed, then the two averages the table does not
 * draw. The summary is on the wire precisely so this can serialise it (D48.18).
 */
export const buildCsv = ({ rows = [], summary = {}, focusLabels = {} } = {}) => {
  const lines = [csvLine(CSV_HEADERS), ...rows.map((row) => csvLine(csvRow(row, focusLabels)))];

  lines.push("");
  const solo = pooledSoloCounts(rows);
  lines.push(
    csvLine([
      "Average Solo Accuracy",
      csvValue(percentText(solo.answered ? (100 * solo.correct) / solo.answered : null)),
      answeredCountLine(solo.correct, solo.answered),
    ])
  );
  lines.push(
    csvLine([
      "Average Collaboration Level",
      csvValue(averageLevelText(summary.average_collab_level)),
      sessionCountLine(summary.collab_session_count, rows.length),
    ])
  );

  return `${lines.join("\n")}\n`;
};

// A name a filesystem will take: no separators, no quotes, no runs of spaces.
const slug = (text) =>
  String(text || "")
    .replace(/[/\\"'<>:|?*]/g, "")
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Names the student, and the class only when one is chosen (D48.10).
 *
 * `suffix` names the TAB when the file is not the Session Detail one, so a
 * teacher who downloads both does not end up with two files of the same name
 * holding different tables (OKO2-3038).
 *
 * The Sessions tab's PDF shares the CSV's stem, so only `extension` differs.
 */
export const csvFilename = ({ studentName, className, suffix, extension = "csv" } = {}) =>
  ["student-report", slug(studentName), slug(className), slug(suffix)]
    .filter(Boolean)
    .join("-") + `.${extension}`;
